import React, { useState } from 'react';
import {
  Container, Paper, Typography, Button, Box, Grid, Card,
  CardActionArea, Divider, Chip,
  FormControl, Select, MenuItem
} from '@mui/material';
import {
  ArrowForward, ArrowBack, BusinessCenter, CheckCircle, Domain,
  Person, VerifiedUser, Security, Star, FlashOn
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';
import ServiceContactForm from '../components/ServiceContactForm';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const startupServicesList = [
  { id: 'all', name: 'All Services', icon: <FlashOn /> },
  { id: 'pvt-ltd', name: 'Private Company', popular: true, docs: ['PAN & Aadhaar', 'Bank Proof', 'Electricity Bill'] },
  { id: 'llp', name: 'LLP Registration', docs: ['Partner PANs', 'Address Proof', 'Rental Agreement'] },
  { id: 'opc', name: 'One Person Company', docs: ['Nominee Consent', 'PAN & Aadhaar', 'Utility Bill'] },
  { id: 'proprietor', name: 'Proprietor Setup', docs: ['Aadhaar', 'GST/MSME', 'Bank Account'] },
  { id: 'trademark', name: 'Trademark Brand Protection', popular: true, docs: ['Logo Image', 'Brand Name', 'TM Authorisation'] },
  { id: 'startup-india', name: 'Startup India (DPIIT)', docs: ['Pitch Deck/Business Plan', 'Incorporation Cert', 'Patent/Innovation Proof'] },
  { id: 'msme', name: 'MSME / Udyam Certificate', docs: ['Aadhaar Card', 'PAN Card', 'Bank IFSC'] },
  { id: 'dsc', name: 'Digital Signature (DSC)', docs: ['Aadhaar Video eKYC', 'PAN Card', 'Photo'] }
];

const planFeatures: Record<string, string[]> = {
  basic: ["Incorporation / Filing Certificate", "Digital Signature Certificate (DSC)", "PAN & TAN Generation", "Standard Email Support"],
  standard: ["Includes Basic Package", "Name Approval Assistance", "AOA & MOA Drafting", "GST Registration", "Priority CA Assistance"],
  premium: ["Includes Standard Package", "Trademark Brand Application", "DPIIT Startup India Registration", "MSME/Udyam Certificate", "Dedicated CA Retainer"]
};

interface Option { label: string; value: string; subtext?: string; icon: React.ReactNode; }
interface Question { id: number; text: string; description: string; options: Option[]; }

const StartupRegistration: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [recommendedEntity, setRecommendedEntity] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'standard' | 'premium'>('standard');

  const questions: Question[] = [
    {
      id: 1,
      text: "How many co-founders or partners are starting this business?",
      description: "Entity structures have minimum & maximum founder requirements under Indian Corporate Law.",
      options: [
        { label: "1 Founder (Sole Owner)", value: "single", subtext: "Best for OPC or Proprietorship", icon: <Person sx={{ fontSize: 36 }} /> },
        { label: "2 to 5 Co-Founders", value: "multi", subtext: "Best for Private Limited or LLP", icon: <BusinessCenter sx={{ fontSize: 36 }} /> },
        { label: "Family Business Members", value: "family", subtext: "Best for HUF, Partnership or Pvt Ltd", icon: <Domain sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 2,
      text: "Do you plan to raise external VC or Angel investment capital?",
      description: "Equity investors & Venture Capital funds in India strictly require a Private Limited Company structure.",
      options: [
        { label: "Yes, actively planning equity funding", value: "yes_vc", subtext: "Requires Private Limited Company for issuing shares", icon: <Star sx={{ fontSize: 36 }} /> },
        { label: "No, self-funded / bootstrapped", value: "no_vc", subtext: "LLP or OPC can save annual compliance costs", icon: <Security sx={{ fontSize: 36 }} /> },
        { label: "Undecided / Future scope", value: "future_vc", subtext: "Incorporate flexible entity structure", icon: <Domain sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 3,
      text: "What is your projected 1st Year Annual Business Turnover?",
      description: "Turnover impacts liability risk protection and GST compliance thresholds.",
      options: [
        { label: "Below ₹20 Lakhs", value: "below_20l", subtext: "Low turnover threshold", icon: <Domain sx={{ fontSize: 36 }} /> },
        { label: "₹20 Lakhs to ₹1 Crore", value: "20l_1cr", subtext: "Moderate scale business", icon: <BusinessCenter sx={{ fontSize: 36 }} /> },
        { label: "Above ₹1 Crore", value: "above_1cr", subtext: "High volume enterprise scale", icon: <Star sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 4,
      text: "Do you require Brand Name & Logo Protection (Trademark)?",
      description: "Trademark registration gives you legal nationwide ownership of your business brand name.",
      options: [
        { label: "Yes, register Trademark too", value: "yes_tm", subtext: "Protect brand name & logo nationwide", icon: <VerifiedUser sx={{ fontSize: 36 }} /> },
        { label: "No, incorporation only for now", value: "no_tm", subtext: "Entity incorporation only", icon: <Domain sx={{ fontSize: 36 }} /> }
      ]
    }
  ];

  const handleOptionSelect = (value: string) => {
    setAnswers(prev => ({ ...prev, [questions[currentStep].id]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      const entity = calculateRecommendedEntity();
      setRecommendedEntity(entity);
      setShowRegistration(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const calculateRecommendedEntity = (): string => {
    const q1 = answers[1];
    const q2 = answers[2];

    if (q2 === 'yes_vc') return 'Pvt Ltd';
    if (q1 === 'single') return 'OPC';
    if (q1 === 'multi') return 'LLP';
    return 'Pvt Ltd';
  };

  const activeEntityKey = recommendedEntity || 'Pvt Ltd';

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <SEOHead
        title="Startup Registration — Pvt Ltd, LLP, OPC, Trademark | Fingard Partners"
        description="Register your startup with Fingard Partners. Expert incorporation services for Private Limited, LLP, One Person Company, and Trademark Registration across India."
        keywords="Startup Registration India, Private Limited Company Registration, LLP Registration, OPC Registration, Trademark Registration"
        canonicalUrl="/startup-registration"
      />
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 4 }}>
        <Chip label="Startup Incorporation Hub" color="primary" sx={{ mb: 1.5, fontWeight: 'bold' }} />
        <Typography variant="h4" fontWeight={800} color="#123D6B" gutterBottom>
          Startup Registration & Incorporation Portal
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto' }}>
          Find the legal structure best suited for your business model with our 4-step diagnostic wizard.
        </Typography>
      </Box>

      {/* DIAGNOSTIC WIZARD SECTION */}
      <Container maxWidth="md">
        {!showRegistration ? (
          <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                Question {currentStep + 1} of {questions.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(((currentStep + 1) / questions.length) * 100)}% Completed
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight={800} color="#123D6B" sx={{ mb: 1 }}>
              {questions[currentStep].text}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {questions[currentStep].description}
            </Typography>

            <Grid container spacing={2}>
              {questions[currentStep].options.map((opt, i) => {
                const isSelected = answers[questions[currentStep].id] === opt.value;
                return (
                  <Grid item xs={12} key={i}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: isSelected ? '#123D6B' : '#E2E8F0',
                        borderWidth: isSelected ? 2 : 1,
                        bgcolor: isSelected ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#C59B27', transform: 'translateY(-2px)' }
                      }}
                    >
                      <CardActionArea onClick={() => handleOptionSelect(opt.value)} sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                          <Box sx={{ color: isSelected ? '#123D6B' : '#64748b' }}>
                            {opt.icon}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight="bold" color="#0f172a" sx={{ fontSize: '1.05rem' }}>
                              {opt.label}
                            </Typography>
                            {opt.subtext && (
                              <Typography variant="body2" color="text.secondary">
                                {opt.subtext}
                              </Typography>
                            )}
                          </Box>
                          {isSelected && <CheckCircle color="primary" />}
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                disabled={currentStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disabled={!answers[questions[currentStep].id]}
                onClick={handleNext}
                endIcon={<ArrowForward />}
                sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, bgcolor: '#123D6B', px: 4, '&:hover': { bgcolor: '#C59B27' } }}
              >
                {currentStep === questions.length - 1 ? 'See Recommendation' : 'Next Step'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Chip label="AI Business Structure Match" color="primary" sx={{ mb: 1.5, fontWeight: 'bold' }} />
                <Typography variant="h5" fontWeight={800} color="#123D6B" gutterBottom>
                  Recommended Entity: {activeEntityKey} Incorporation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Based on your responses, <strong>{activeEntityKey}</strong> offers the optimal liability protection & growth capability.
                </Typography>

                {/* SERVICE OVERRIDE DROPDOWN */}
                <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'left', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                    Want a different entity or service? Choose from dropdown:
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={activeEntityKey}
                      onChange={(e) => setRecommendedEntity(e.target.value)}
                      sx={{ borderRadius: 2.5, bgcolor: '#F8FAFC', fontWeight: 600, color: '#123D6B' }}
                    >
                      <MenuItem value="Pvt Ltd">Private Limited Company (Pvt Ltd)</MenuItem>
                      <MenuItem value="LLP">Limited Liability Partnership (LLP)</MenuItem>
                      <MenuItem value="OPC">One Person Company (OPC)</MenuItem>
                      <MenuItem value="Proprietorship">Proprietorship Setup</MenuItem>
                      <MenuItem value="Trademark">Trademark Brand Protection</MenuItem>
                      <MenuItem value="Startup India (DPIIT)">Startup India (DPIIT Recognition)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Package Tiers Selection (Scope only, no public prices) */}
              <Typography variant="subtitle1" fontWeight="bold" color="#0f172a" sx={{ mb: 1.5 }}>
                Select Registration Package Scope:
              </Typography>

              <Grid container spacing={2} sx={{ mb: 1 }}>
                {(['basic', 'standard', 'premium'] as const).map((plan) => {
                  const isSel = selectedPlan === plan;
                  return (
                    <Grid item xs={12} sm={4} key={plan}>
                      <Card
                        variant="outlined"
                        onClick={() => setSelectedPlan(plan)}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          borderRadius: 3,
                          borderColor: isSel ? '#123D6B' : '#E2E8F0',
                          borderWidth: isSel ? 2 : 1,
                          bgcolor: isSel ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        {plan === 'standard' && (
                          <Chip label="Most Popular" color="secondary" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold' }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'uppercase', mt: 1 }}>
                          {plan} Package
                        </Typography>
                        <Box sx={{ my: 1.5 }}>
                          <Chip label="Custom CA Quote" size="small" sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700 }} />
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        {planFeatures[plan].map((feat, idx) => (
                          <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5, textAlign: 'left' }}>
                            ✓ {feat}
                          </Typography>
                        ))}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Service Contact & Quotation Request Form */}
            <ServiceContactForm
              serviceCategory="startups"
              selectedService={activeEntityKey + ' Incorporation (' + selectedPlan.toUpperCase() + ' Scope)'}
              selectedPackageTier={selectedPlan}
              diagnosticAnswers={answers}
              onBackToQuiz={() => setShowRegistration(false)}
            />
          </Box>
        )}
      </Container>
    </Container>
  );
};

export default StartupRegistration;
